/**
 * Upload a file to S3 using a presigned URL, without aws-sdk v2.
 * It relies on an existing `getSignedUrl(file, cb)` function to retrieve the URL.
 *
 * @param file - The file to upload
 * @param getSignedUrl - Function that fetches a presigned URL; signature: (file: File, cb: (obj: { signedUrl: string }) => void) => void
 * @param headers - Optional headers to include in the PUT request (e.g., { 'Content-Type': file.type })
 * @returns Promise resolving with the SignedUrlObject returned by `getSignedUrl`
 * @throws Error when the upload fails
 * @author GitHub Copilot
 */
export const uploadFileWithSignedUrl = async (
  file: File,
  getSignedUrl: (file: File, cb: (obj: { signedUrl: string }) => void) => void,
  headers?: Record<string, string>
): Promise<{ signedUrl: string }> => {
  const signed = await new Promise<{ signedUrl: string }>((resolve, reject) => {
    try {
      getSignedUrl(file, resolve);
    } catch (err) {
      reject(err);
    }
  });

  const response = await fetch(signed.signedUrl, {
    method: 'PUT',
    body: file,
    headers: headers || {},
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  return signed;
};
